import { z } from "zod";
import palettes from "../../../assets/report-themes/palettes.json";

// The same original nine classes and 27 trees used by the report theme profile.
export const plannerClasses = palettes.classes
  .filter((entry) => String(entry.id) in palettes.profiles.classic.classes)
  .map((entry) => ({
    name: entry.name,
    colour: entry.color,
    specs: palettes.profiles.classic.classes[
      String(entry.id) as keyof typeof palettes.profiles.classic.classes
    ] as readonly string[],
  }));
export const planningRoles = [
  "tank",
  "healer",
  "melee",
  "ranged",
  "unspecified",
] as const;
export const roleLabels: Record<PlanningRole, string> = {
  tank: "Tank",
  healer: "Healer",
  melee: "Melee",
  ranged: "Ranged",
  unspecified: "Unassigned role",
};
export type PlanningRole = (typeof planningRoles)[number];
const id = z.string().uuid();
const size = z.number().int().min(5).max(40).multipleOf(5);
const profile = z.enum(["forever", "classic"]);
const attribution = z
  .object({
    userId: z.string().regex(/^\d{15,22}$/),
    displayName: z.string().max(100),
  })
  .strict();
export const entrySchema = z
  .object({
    id,
    name: z.string().trim().min(1).max(40),
    className: z.string().max(30),
    spec: z.string().max(30),
    role: z.enum(planningRoles),
    note: z.string().max(160),
    slot: z.number().int().min(0).max(39).nullable(),
  })
  .strict()
  .refine(
    (entry) =>
      plannerClasses.some(
        (c) => c.name === entry.className && c.specs.includes(entry.spec),
      ),
    "Choose a supported class and talent tree",
  );
export type PlanningEntry = z.infer<typeof entrySchema>;
export type PlanningLease = { connectionId: string; expiresInMs: number };
export const boardSchema = z
  .object({
    schemaVersion: z.literal(1),
    id,
    guildId: z.string(),
    title: z.string().trim().min(1).max(100),
    profile,
    size,
    kind: z.enum(["raid", "template"]),
    notes: z.string().max(2000),
    locked: z.boolean(),
    revision: z.number().int().positive(),
    createdAt: z.number().int(),
    updatedAt: z.number().int(),
    createdBy: attribution,
    updatedBy: attribution,
    source: z
      .object({ id, revision: z.number().int().positive() })
      .strict()
      .nullable(),
    entries: z.array(entrySchema).max(80),
  })
  .strict()
  .superRefine((board, ctx) => {
    const ids = new Set<string>(),
      slots = new Set<number>();
    for (const entry of board.entries) {
      if (ids.has(entry.id))
        ctx.addIssue({ code: "custom", message: "Duplicate planning entry" });
      ids.add(entry.id);
      if (entry.slot !== null) {
        if (entry.slot >= board.size || slots.has(entry.slot))
          ctx.addIssue({
            code: "custom",
            message: "Invalid or occupied raid slot",
          });
        slots.add(entry.slot);
      }
    }
  });
export type PlanningBoard = z.infer<typeof boardSchema>;
export type BoardSummary = Pick<
  PlanningBoard,
  | "id"
  | "title"
  | "profile"
  | "size"
  | "kind"
  | "locked"
  | "revision"
  | "createdBy"
  | "updatedAt"
  | "updatedBy"
> & { placed: number; bench: number; occupiedSlots: number[] };
export function summarizeBoard(board: PlanningBoard): BoardSummary {
  const {
    id,
    title,
    profile,
    size,
    kind,
    locked,
    revision,
    createdBy,
    updatedAt,
    updatedBy,
  } = board;
  const placed = board.entries.filter((entry) => entry.slot !== null).length;
  return {
    id,
    title,
    profile,
    size,
    kind,
    locked,
    revision,
    createdBy,
    updatedAt,
    updatedBy,
    placed,
    bench: board.entries.length - placed,
    occupiedSlots: board.entries.flatMap((entry) =>
      entry.slot === null ? [] : [entry.slot],
    ),
  };
}
export const createBoardSchema = z
  .object({
    id,
    title: z.string().trim().min(1).max(100),
    profile,
    size,
    kind: z.enum(["raid", "template"]),
    source: z
      .object({ id, revision: z.number().int().positive() })
      .strict()
      .optional(),
  })
  .strict();
export type CreateBoard = z.infer<typeof createBoardSchema>;
export const operationSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("add"), entry: entrySchema }).strict(),
  z.object({ type: z.literal("edit"), entry: entrySchema }).strict(),
  z
    .object({
      type: z.literal("move"),
      entryId: id,
      slot: z.number().int().min(0).max(39).nullable(),
    })
    .strict(),
  z.object({ type: z.literal("remove"), entryId: id }).strict(),
  z
    .object({
      type: z.literal("details"),
      title: z.string().trim().min(1).max(100),
      size,
      notes: z.string().max(2000),
    })
    .strict(),
  z.object({ type: z.literal("lock"), locked: z.boolean() }).strict(),
  z
    .object({
      type: z.literal("restore"),
      revision: z.number().int().positive(),
    })
    .strict(),
]);
export type BoardOperation = z.infer<typeof operationSchema>;
export const editBoardSchema = z
  .object({
    operationId: id,
    expectedRevision: z.number().int().positive(),
    operation: operationSchema,
  })
  .strict();
export type EditBoard = z.infer<typeof editBoardSchema>;
export interface HistoryEntry {
  revision: number;
  at: number;
  actor: { userId: string; displayName: string };
  summary: string;
}
export class PlannerError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Pure planning changes. This never creates identities, assignments or attendance. */
export function changeBoard(
  current: PlanningBoard,
  operation: BoardOperation,
  manager: boolean,
  restored?: PlanningBoard,
): { board: PlanningBoard; summary: string } {
  if ((operation.type === "lock" || operation.type === "restore") && !manager)
    throw new PlannerError(
      403,
      "A current raid leader or officer must make this change",
    );
  if (current.locked && operation.type !== "lock")
    throw new PlannerError(
      409,
      "This version is locked. A raid leader can unlock it, or you can make a copy",
    );
  const board = structuredClone(current);
  let summary = "";
  switch (operation.type) {
    case "add": {
      if (board.entries.some((entry) => entry.id === operation.entry.id))
        throw new PlannerError(409, "That planning entry already exists");
      if (board.entries.length >= 80)
        throw new PlannerError(
          400,
          "A board can hold 80 entries including the bench",
        );
      if (
        operation.entry.slot !== null &&
        board.entries.some((entry) => entry.slot === operation.entry.slot)
      )
        throw new PlannerError(
          409,
          "That slot is occupied. Select an empty slot or add to the bench",
        );
      board.entries.push(operation.entry);
      summary = "Added " + operation.entry.name;
      break;
    }
    case "edit": {
      const index = board.entries.findIndex(
        (entry) => entry.id === operation.entry.id,
      );
      if (index < 0)
        throw new PlannerError(409, "That planning entry was removed");
      // Moving has its own operation with explicit swap semantics.
      board.entries[index] = {
        ...operation.entry,
        slot: board.entries[index]!.slot,
      };
      summary = "Edited " + operation.entry.name;
      break;
    }
    case "move": {
      const entry = board.entries.find(
        (entry) => entry.id === operation.entryId,
      );
      if (!entry)
        throw new PlannerError(409, "That planning entry was removed");
      const other =
        operation.slot === null
          ? undefined
          : board.entries.find((entry) => entry.slot === operation.slot);
      if (other && other.id !== entry.id) other.slot = entry.slot;
      entry.slot = operation.slot;
      summary =
        "Moved " +
        entry.name +
        (operation.slot === null
          ? " to the bench"
          : " to group " + (Math.floor(operation.slot / 5) + 1)) +
        (other && other.id !== entry.id ? " | swapped with " + other.name : "");
      break;
    }
    case "remove": {
      const entry = board.entries.find(
        (entry) => entry.id === operation.entryId,
      );
      if (!entry)
        throw new PlannerError(409, "That planning entry was already removed");
      board.entries = board.entries.filter(
        (entry) => entry.id !== operation.entryId,
      );
      summary = "Removed " + entry.name;
      break;
    }
    case "details": {
      Object.assign(board, {
        title: operation.title,
        notes: operation.notes,
        size: operation.size,
      });
      for (const entry of board.entries)
        if (entry.slot !== null && entry.slot >= board.size) entry.slot = null;
      summary =
        "Updated board details" +
        (operation.size < current.size ? " | overflow moved to bench" : "");
      break;
    }
    case "lock":
      board.locked = operation.locked;
      summary = operation.locked
        ? "Locked this planning version"
        : "Unlocked the board";
      break;
    case "restore": {
      if (
        !restored ||
        restored.id !== current.id ||
        restored.profile !== current.profile
      )
        throw new PlannerError(404, "That saved revision is unavailable");
      Object.assign(board, {
        title: restored.title,
        size: restored.size,
        notes: restored.notes,
        entries: structuredClone(restored.entries),
      });
      summary = "Restored the composition from revision " + operation.revision;
      break;
    }
  }
  const parsed = boardSchema.safeParse(board);
  if (!parsed.success)
    throw new PlannerError(
      400,
      parsed.error.issues[0]?.message ?? "Invalid composition",
    );
  return { board: parsed.data, summary };
}
